import { pushNotificationManager } from './pushManager';

/**
 * Initialize push notifications on app load
 * Automatically subscribes if permission is already granted
 */
export async function initializePushNotifications() {

  // Check if notifications are supported
  if (!pushNotificationManager.checkSupport()) {
    return false;
  }

  // Check current permission status
  const permission = pushNotificationManager.getPermissionStatus();

  // If permission is already granted, auto-subscribe (or re-sync with backend)
  if (permission === 'granted' && pushNotificationManager.canAutoSubscribe()) {
    try {
      // Always call subscribeToPushNotifications — it will re-use the existing
      // browser subscription but re-send it to the backend, ensuring the backend
      // record is never stale.
      return Boolean(await pushNotificationManager.subscribeToPushNotifications());
    } catch (error) {
      console.error('[Notifications] Error auto-subscribing:', error);
      return false;
    }
  }

  // Permission not granted yet - don't auto-request
  // Let the user enable it manually via settings or banner
  return false;
}

/**
 * Check and update subscription status periodically
 * Useful for detecting when subscription expires or gets revoked
 */
export function startSubscriptionMonitoring(intervalMs = 60000) {
  let checking = false;
  const monitor = setInterval(async () => {
    if (checking) return;
    checking = true;
    try {
      const permission = pushNotificationManager.getPermissionStatus();
      
      // If permission changed to denied, clear subscription status
      if (permission === 'denied') {
        localStorage.removeItem('pushSubscriptionActive');
        return;
      }

      // If permission is granted, check subscription validity
      if (permission === 'granted' && pushNotificationManager.canAutoSubscribe()) {
        // Browser state and localStorage do not prove that Sena has an active
        // registration. Retry even when the initial registration never succeeded.
        await pushNotificationManager.ensureRegistration();
      }
    } catch (error) {
      console.error('[Notifications] Error in subscription monitoring:', error);
    } finally {
      checking = false;
    }
  }, intervalMs);

  // Return cleanup function
  return () => {
    clearInterval(monitor);
  };
}

/** Re-sync an existing subscription after a worker update without rotating it.
 * Multiple tabs can safely perform this idempotent operation. Rotating an endpoint
 * from competing tabs can otherwise leave the server holding an invalid endpoint.
 */
export function setupServiceWorkerUpdateHandler() {
  if (!('serviceWorker' in navigator)) return undefined;
  const handleControllerChange = async () => {
    if (!pushNotificationManager.canAutoSubscribe()) return;
    try {
      await pushNotificationManager.subscribeToPushNotifications();
    } catch (error) {
      console.error('[Notifications] Error re-syncing after service worker update:', error);
    }
  };
  navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
  return () => navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
}

/**
 * Complete initialization - call this on app startup
 */
export async function initializeNotifications() {
  try {
    // Initialize push notifications
    await initializePushNotifications();

    // Setup service worker update handler
    const stopUpdates = setupServiceWorkerUpdateHandler();

    // Start subscription monitoring (check every minute)
    const stopMonitoring = startSubscriptionMonitoring(60000);

    // Return cleanup function
    return () => { stopMonitoring(); stopUpdates?.(); };
  } catch (error) {
    console.error('[Notifications] Error in notification initialization:', error);
    return () => {}; // Return empty cleanup function
  }
}

export default initializeNotifications;
